import { LEGACY_PLACEHOLDER_KEYS, PLACEHOLDERS_KEY } from '$lib/global/constants';

import getQueryParameter from '$lib/global/helper/get-query-parameter';
import { setTrackingProperty } from '$lib/helper';

const storageKey = (campaignId: string) => `${PLACEHOLDERS_KEY}${campaignId}`;

/**
 * Move a previous build's entry to the current key.
 *
 * sessionStorage outlives a deploy inside an open tab, so without this a reload onto a newer
 * build would drop placeholder values collected earlier in the visit and leave behind an
 * orphan entry under a name the cookie policy no longer lists. The newest legacy name wins,
 * and every legacy entry is removed either way.
 */
const migrateLegacyStore = (campaignId: string): void => {
    const ST = window.sessionStorage;

    LEGACY_PLACEHOLDER_KEYS.forEach((prefix) => {
        const legacyKey = `${prefix}${campaignId}`;
        const legacyValue = ST.getItem(legacyKey);

        if (legacyValue === null) return;

        if (!ST.getItem(storageKey(campaignId))) {
            ST.setItem(storageKey(campaignId), legacyValue);
        }

        ST.removeItem(legacyKey);
    });
};

/**
 * Seed placeholder values (text personalization + HTML block variable interpolation) from
 * URL query params (`ps_*` / `perspective_*` and `utm_*`).
 *
 * FUN-3812: This intentionally does NOT reset the stored properties.
 * Clearing is session-scoped and owned by TrackingService
 * (`createNewSession` → `clearPersonalizationData` + `seedTrackingPropertiesFromUrl`,
 * see FUN-3582). A blind reset on every hard load destroyed personalization
 * collected earlier in the funnel (e.g. opt-in form answers) whenever a lead
 * returned via a full page load — for instance after being sent to an external
 * site and redirected back. We only merge URL params on top of whatever the
 * (possibly restored) session already holds.
 */
export const seedTrackingPropertiesFromQuery = (campaignId: string): void => {
    const queryParams = getQueryParameter();

    migrateLegacyStore(campaignId);

    // No empty scaffold: a visit that carries no placeholder values writes nothing at all.
    // Every reader (`getAllTrackingProperties`, `setTrackingProperty`) already treats a
    // missing key as `{}`, and `TrackingService.clearPersonalizationData` relies on the key
    // staying absent when a regenerated session has nothing to re-seed.
    Object.keys(queryParams.custom).forEach((param) => {
        const value = queryParams.custom[param];
        setTrackingProperty(param, typeof value === 'string' ? value : undefined, campaignId);
    });

    // FUN-800: We want to support HTML Block code interpolation for UTM parameters too.
    Object.keys(queryParams.utm).forEach((param) => {
        const value = queryParams.utm[param];
        setTrackingProperty(param, typeof value === 'string' ? value : undefined, campaignId);
    });
};
